import 'twin.macro';
import React from 'react';
import { CJFilterStatus } from '@teetsh/app/src/types/event';
import Popover from '@teetsh/app/src/components/Popover';
import { Radios } from '@teetsh/app/src/components/Form/Radios';
import { Checkboxes } from '@teetsh/app/src/components/Form/Checkboxes';
import useClassrooms from '@teetsh/app/src/containers/App/context/ClassroomsContext/useClassrooms';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { useCJConfigurationDisplay } from '@teetsh/app/src/containers/CahierJournal/useCJConfigurationDisplay';
import { SwitchToggle } from 'components/Form/SwitchToggle';
import { useTranslation } from 'react-i18next';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import { FieldsToDisplay } from './FieldsToDisplay';
import { AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import Tooltip2 from '@teetsh/app/src/components/Tooltip2';

interface Props {
  className?: string;
}

export function Filters(props: Props) {
  const { className } = props;
  const {
    classroomsId,
    status,
    setClassroomsId,
    setStatus,
    hideEvents,
    hideBirthdays,
    setHideBirthdays,
    setHideEvents,
  } = useCJConfigurationDisplay();
  const classrooms = useClassrooms();
  const { guestMode } = useUser();
  const hasAllClassroomsSelected =
    classroomsId.length === classrooms.length || classroomsId.length === 0;
  const { t } = useTranslation(['common', 'cahierJournal']);
  return (
    <Popover portal offsetInPx={8}>
      <Tooltip2 label={t('cahierJournal:toolbar.configureView')}>
        <Popover.Button
          icon={AdjustmentsHorizontalIcon}
          aria-label="Filtres cahier journal"
          intent={
            status !== 'all' || !hasAllClassroomsSelected ? 'primary' : 'none'
          }
          className={className}
          minimal
          iconSize="lg"
        />
      </Tooltip2>
      <Popover.Panel size="xl">
        <div>
          {!guestMode?.enabled && (
            <div tw="px-5 py-3 border-b border-neutral-quiet">
              <FormGroup
                label={t('cahierJournal:toolbar.filter.elements.label')}
              >
                <FieldsToDisplay withStatus />
              </FormGroup>

              <div>
                <SwitchToggle
                  size="xs"
                  label={t('cahierJournal:toolbar.filter.elements.birthdays')}
                  onChange={v => setHideBirthdays(!v)}
                  checked={!hideBirthdays}
                />
                <SwitchToggle
                  size="xs"
                  label={t('cahierJournal:toolbar.filter.elements.events')}
                  onChange={v => setHideEvents(!v)}
                  checked={!hideEvents}
                />
              </div>
            </div>
          )}
          <div tw="px-5 py-3 border-b border-neutral-quiet">
            <FormGroup
              withoutMargin
              label={t('cahierJournal:toolbar.filter.status.label')}
            >
              <Radios<CJFilterStatus>
                value={status}
                onChange={v => setStatus(v)}
                options={[
                  {
                    value: 'all',
                    label: (
                      <span tw="font-normal text-neutral-intense">
                        {t('cahierJournal:toolbar.filter.status.all')}
                      </span>
                    ),
                  },
                  {
                    value: 'done',
                    label: (
                      <span tw="font-normal text-neutral-intense">
                        {t('cahierJournal:toolbar.filter.status.done')}
                      </span>
                    ),
                  },
                  {
                    value: 'open',
                    label: (
                      <span tw="font-normal text-neutral-intense">
                        {t('cahierJournal:toolbar.filter.status.notDone')}
                      </span>
                    ),
                  },
                ]}
              />
            </FormGroup>
          </div>
          {classrooms.length > 1 && (
            <div tw="p-5 py-3">
              <FormGroup
                withoutMargin
                label={t('cahierJournal:toolbar.filter.classrooms.label')}
              >
                <Checkboxes
                  condensed
                  value={classroomsId}
                  onChange={v => setClassroomsId(v)}
                  options={classrooms.map(cl => ({
                    label: cl.name,
                    value: cl.id,
                  }))}
                />
              </FormGroup>
            </div>
          )}
        </div>
      </Popover.Panel>
    </Popover>
  );
}
